import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Users, Group, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUpdateWidget } from "@/hooks";
import { Widget } from "@/types/dashboard.types";

interface EditWidgetGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  widget: Widget | null;
  availableWidgets: Widget[];
  onUpdated?: (widget: Widget, originalChildrenIds: string[]) => void;
  onEditWidget?: (widgetId: string) => void;
}

export default function EditWidgetGroupModal({
  isOpen,
  onClose,
  widget,
  availableWidgets,
  onUpdated,
  onEditWidget,
}: EditWidgetGroupModalProps) {
  const { t } = useTranslation();
  const updateWidgetMutation = useUpdateWidget();

  const [groupName, setGroupName] = useState("");
  const [selectedWidgetIds, setSelectedWidgetIds] = useState<string[]>([]);
  const [originalChildrenIds, setOriginalChildrenIds] = useState<string[]>([]);

  useEffect(() => {
    if (widget && isOpen) {
      setGroupName(widget.title || "");
      const config = (widget as any).config as any;
      const childrenIds = config?.group?.childrenIds || [];
      setSelectedWidgetIds([...childrenIds]);
      setOriginalChildrenIds([...childrenIds]);
    }
  }, [widget, isOpen]);

  const validateForm = (): boolean => {
    if (!groupName.trim()) {
      toast.error(t('processManagement.groupNameRequired'));
      return false;
    }

    if (selectedWidgetIds.length < 2) {
      toast.error(t('processManagement.selectAtLeastTwoWidgets'));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !widget) return;

    try {
      const updateRes = await updateWidgetMutation.mutateAsync({
        id: widget.id,
        formData: {
          title: groupName.trim(),
          config: {
            group: {
              childrenIds: selectedWidgetIds,
            },
          },
        },
      });

      const updated =
        (updateRes as any)?.data?.data ??
        (updateRes as any)?.data ??
        updateRes ??
        { ...widget, title: groupName.trim(), config: { group: { childrenIds: selectedWidgetIds } } };

      onUpdated?.(updated, originalChildrenIds);
      toast.success(t('processManagement.groupUpdatedSuccessfully'));
      handleClose();
    } catch (error) {
      toast.error(t('processManagement.failedToUpdateGroup'));
      console.error("Update widget group error:", error);
    }
  };

  const handleClose = () => {
    setGroupName("");
    setSelectedWidgetIds([]);
    setOriginalChildrenIds([]);
    onClose();
  };

  const handleWidgetToggle = (widgetId: string, checked: boolean) => {
    setSelectedWidgetIds(prev =>
      checked
        ? [...prev, widgetId]
        : prev.filter(id => id !== widgetId)
    );
  };

  // Filter out the current group widget and any other group widgets from available widgets
  const selectableWidgets = availableWidgets.filter(w =>
    w.id !== widget?.id && w.visualizationType !== "group"
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Group className="w-5 h-5" />
            {t('processManagement.editWidgetGroup')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Group Name */}
          <div className="space-y-2">
            <Label htmlFor="groupName">{t('processManagement.groupName')} *</Label>
            <Input
              id="groupName"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('processManagement.enterGroupName')}
              className="w-full"
            />
          </div>

          {/* Widget Selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>{t('processManagement.selectWidgetsForGroup')}</Label>
              <span className="text-sm text-gray-500">
                {selectedWidgetIds.length} {t('processManagement.selected')}
              </span>
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-lg p-4 space-y-3">
              {selectableWidgets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{t('processManagement.noWidgetsAvailable')}</p>
                </div>
              ) : (
                selectableWidgets.map((w) => (
                  <div key={w.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={w.id}
                      checked={selectedWidgetIds.includes(w.id)}
                      onCheckedChange={(checked) =>
                        handleWidgetToggle(w.id, !!checked)
                      }
                    />
                    <label
                      htmlFor={w.id}
                      className="flex-1 text-sm font-medium cursor-pointer"
                    >
                      {w.title}
                    </label>
                    <span className="text-xs text-gray-500 capitalize">
                      {w.visualizationType}
                    </span>
                    {onEditWidget && (
                      <Button
                        onClick={() => onEditWidget(w.id)}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-muted"
                        title={t('processManagement.editWidget')}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>

            {selectedWidgetIds.length < 2 && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                {t('processManagement.selectAtLeastTwoWidgets')}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateWidgetMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={updateWidgetMutation.isPending || selectedWidgetIds.length < 2}
              className="main-dark-button"
            >
              {updateWidgetMutation.isPending ? t('common.updating') : t('common.update')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
